import equal from "fast-deep-equal/es6";

import { defined } from "@formant/core/common/defined";

import { localSettingObservable } from "../utils/localSettingObservable";
import { IAuthStore } from "./IAuthStore";
import { IChannelStore } from "./IChannelStore";
import { IDeviceDetailsStore } from "./IDeviceDetailsStore";
import { IGroupStore } from "./IGroupStore";
import { IRouterStore } from "./IRouterStore";
import { IRtcSignalingStore } from "./IRtcSignalingStore";
import { ISignalingStore } from "./ISignalingStore";
import { StoreCache } from "./StoreCache";

export interface IJumpSuggestion {
    type: "device" | "channel" | "board" | "teleop" | "group";
    title: string;
    to: string;
}

export class JumpStore {
    @localSettingObservable("jump.recent")
    private recent: IJumpSuggestion[] = [];
    private suggestionCache = new StoreCache<void, IJumpSuggestion[]>();

    private readonly authStore: IAuthStore;
    private readonly channelStore: IChannelStore;
    private readonly deviceDetailsStore: IDeviceDetailsStore;
    private readonly groupStore: IGroupStore;
    private readonly routerStore: IRouterStore;
    private readonly rtcSignalingStore: IRtcSignalingStore;
    private readonly signalingStore: ISignalingStore;

    constructor({
        authStore,
        channelStore,
        deviceDetailsStore,
        groupStore,
        routerStore,
        rtcSignalingStore,
        signalingStore,
    }: {
        authStore: IAuthStore;
        channelStore: IChannelStore;
        deviceDetailsStore: IDeviceDetailsStore;
        groupStore: IGroupStore;
        routerStore: IRouterStore;
        rtcSignalingStore: IRtcSignalingStore;
        signalingStore: ISignalingStore;
    }) {
        this.authStore = authStore;
        this.channelStore = channelStore;
        this.deviceDetailsStore = deviceDetailsStore;
        this.groupStore = groupStore;
        this.routerStore = routerStore;
        this.rtcSignalingStore = rtcSignalingStore;
        this.signalingStore = signalingStore;
    }

    public suggestions(query: string): IJumpSuggestion[] | undefined {
        const suggestions = this.entries();

        if (!suggestions) {
            return undefined;
        }

        if (query === "") {
            return this.recent.filter(_ =>
                suggestions.some(suggestion => equal(_, suggestion)),
            );
        }

        const startsWithMatches = (suggestions || []).filter(_ =>
            _.title.toLowerCase().startsWith(query.toLowerCase()),
        );

        const includesMatches =
            query.length > 2
                ? (suggestions || [])
                      .filter(_ =>
                          _.title.toLowerCase().includes(query.toLowerCase()),
                      )
                      .filter(
                          _ =>
                              !startsWithMatches.some(suggestion =>
                                  equal(suggestion, _),
                              ),
                      )
                : [];

        return [...startsWithMatches, ...includesMatches].slice(0, 10);
    }

    public saveRecent(suggestion: IJumpSuggestion) {
        this.recent = [
            suggestion,
            ...this.recent.filter(_ => !equal(_, suggestion)),
        ].slice(0, 10);
    }

    private entries(): IJumpSuggestion[] | undefined {
        const { routeTo } = this.routerStore;

        const devices = this.deviceDetailsStore.list({
            enabled: true,
            type: "default",
        });
        if (devices === undefined) {
            return undefined;
        }

        const channels = this.channelStore.list();
        if (channels === undefined) {
            return undefined;
        }

        const peerDevices = this.authStore.hasAccess(
            "portForwarding",
            "operator",
        )
            ? [
                  ...(this.rtcSignalingStore.peerDevices({
                      onlyTeleopable: true,
                  }) || []),
                  ...(this.signalingStore.peerDevices() || []),
              ]
            : [];

        if (peerDevices === undefined) {
            return undefined;
        }

        const groups = this.groupStore.list();
        if (groups === undefined) {
            return undefined;
        }

        return this.suggestionCache.get(undefined, async () => {
            return [
                ...devices.map(({ id, name }) => ({
                    type: "device" as const,
                    title: name,
                    to: routeTo.device({ id: defined(id) }),
                })),
                ...channels.map(({ id, name }) => ({
                    type: "channel" as const,
                    title: name,
                    to: routeTo.channel({ id: defined(id) }),
                })),
                ...peerDevices.map(({ id, name }) => ({
                    type: "teleop" as const,
                    title: name,
                    to: routeTo.teleop({ id: defined(id) }),
                })),
                ...groups.map(({ id, name }) => ({
                    type: "group" as const,
                    title: name,
                    to: routeTo.home(undefined, { groupId: defined(id) }),
                })),
            ].sort((a, b) => a.title.localeCompare(b.title));
        });
    }
}
